import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PageHeader, DataCheckChip, KpiCard, KpiCardSkeleton } from '../components/ds';
const toneFromColor = (c = '') => c.includes('emerald') || c.includes('green') ? 'success' : c.includes('rose') || c.includes('red') ? 'danger' : c.includes('amber') || c.includes('orange') ? 'warning' : c.includes('violet') || c.includes('purple') || c.includes('indigo') ? 'primary' : c.includes('teal') || c.includes('cyan') || c.includes('blue') ? 'teal' : 'default';

import { toast } from 'sonner';
import { 
  Users, DollarSign, Filter, CheckCircle, Target, UserX, Sparkles, Baby, Download
} from 'lucide-react';
import DateRangeFilter from '../components/DateRangeFilter';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import ViewLeadsModal from '../components/ViewLeadsModal';
import { getApiUrl } from '../utils/api';
import { gatedDownload } from '../lib/gatedDownload';
import { useAuth } from '../context/AuthContext';

const API_URL = getApiUrl();

// V42.6 — native per-currency formatting (no FX conversion)
const CUR_SYMBOL = { INR: '₹', USD: '$', CAD: 'C$', AUD: 'A$', EUR: '€', GBP: '£', AED: 'AED ', SGD: 'S$' };
const fmtCur = (cur, amt) =>
  `${CUR_SYMBOL[cur] || (cur + ' ')}${Number(amt || 0).toLocaleString(cur === 'INR' ? 'en-IN' : 'en-US')}`;

// V42.6 — Revenue group: one card PER CURRENCY (New Sales / Renewals / Products)
const RevenueGroup = ({ title, subtitle, byCurrency, inrEquiv, color, onCardClick, testidPrefix, countByCurrency, ctaLabel, icon: Icon = DollarSign, badge }) => {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const cards = entries.length ? entries : [['INR', 0]];
  const multi = entries.length > 1;
  return (
    <div data-testid={`${testidPrefix}-group`}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <span className="text-xs text-slate-500">{subtitle}</span>
          {badge != null && (
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid={`${testidPrefix}-count`}>{badge}</span>
          )}
        </div>
        {multi && inrEquiv != null && (
          <span className="text-xs text-slate-500" data-testid={`${testidPrefix}-inr-equiv`}>
            ≈ ₹{Number(inrEquiv || 0).toLocaleString('en-IN')} <span className="text-slate-400">blended</span>
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={`${testidPrefix}-cards`}>
        {cards.map(([cur, amt]) => (
          <button
            key={cur}
            onClick={() => onCardClick(cur)}
            data-testid={`${testidPrefix}-card-${cur}`}
            className="text-left bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md hover:border-slate-300 transition-all"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{cur}</span>
              <span className={`w-7 h-7 rounded-lg ${color} flex items-center justify-center`}>
                <Icon className="w-4 h-4 text-white" />
              </span>
            </div>
            <div className="mt-2 text-xl font-bold text-slate-900">{fmtCur(cur, amt)}</div>
            {(countByCurrency || ctaLabel) && (
              <div className="mt-1 text-[11px] text-slate-500" data-testid={`${testidPrefix}-card-${cur}-meta`}>
                {countByCurrency ? `${countByCurrency[cur] || 0} purchase${(countByCurrency[cur] || 0) === 1 ? '' : 's'}` : null}
                {countByCurrency && ctaLabel ? ' · ' : null}
                {ctaLabel ? <span className="text-primary font-medium">{ctaLabel} →</span> : null}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const SalesDashboard = () => {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [viewLeadsModal, setViewLeadsModal] = useState(null);
  const [downloadingRows, setDownloadingRows] = useState(false);

  useEffect(() => {
    fetchUserRole();
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      // API returns array directly, not {users: [...]}
      const allUsers = Array.isArray(response.data) ? response.data : (response.data.users || []);
      const salesUsers = allUsers.filter(u => u.role === 'sales');
      setUsers(salesUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedSource.length) params.append('source', selectedSource.join(','));
      if (selectedCountry.length) params.append('country', selectedCountry.join(','));

      const response = await axios.get(
        `${API_URL}/api/analytics/sales-dashboard?${params.toString()}`,
        { withCredentials: true }
      );
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchStats();
  };

  // V82 — Super Admin only. Downloads every row behind the cards for the filters
  // currently applied, to reconcile the dashboard against the Export section.
  const downloadDashboardRows = async () => {
    setDownloadingRows(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedSource.length) params.append('source', selectedSource.join(','));
      if (selectedCountry.length) params.append('country', selectedCountry.join(','));
      const saved = await gatedDownload(
        `/api/analytics/sales-dashboard/export?${params.toString()}`,
        `sales_dashboard_${startDate || 'all'}_to_${endDate || 'all'}.csv`);
      if (saved) toast.success('Dashboard rows downloaded');
    } catch (error) {
      console.error('Dashboard export error:', error);
      toast.error('Failed to download dashboard rows');
    } finally {
      setDownloadingRows(false);
    }
  };

  const openSalesLeadsModal = (title, metric, currency, extra = {}) => {
    setViewLeadsModal({
      title,
      filters: {
        metric,
        start_date: startDate,
        end_date: endDate,
        apiBase: '/api/analytics/sales-dashboard/leads',
        user_id: selectedUser,
        ...(currency ? { currency } : {}),
        ...extra,
      },
    });
  };

  const StatCard = ({ title, value, icon, color, subtitle, onClick, tooltip }) => (
    <KpiCard
      title={title}
      value={value}
      icon={icon}
      tone={toneFromColor(color)}
      subtitle={onClick ? (subtitle ? `${subtitle} · View leads →` : 'View leads →') : subtitle}
      onClick={onClick}
      tooltip={tooltip ? (
        <span data-testid={`tooltip-${title.toLowerCase().replace(/\s+/g, '-')}`}>
          <b>Formula:</b> {tooltip.formula}<br /><b>Source:</b> {tooltip.source}{tooltip.note ? <><br />{tooltip.note}</> : null}
        </span>
      ) : null}
      testId={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    />
  );

  if (loading && !stats) {
    return (
      <div className="space-y-6" data-testid="sales-dashboard-loading">
        <div className="h-9 w-64 shimmer rounded-md" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <KpiCardSkeleton key={i} />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      <PageHeader
        title="Sales Dashboard"
        description="Track consultations, conversions, and revenue"
        actions={<DataCheckChip startDate={startDate || undefined} endDate={endDate || undefined} />}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangeFilter
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            label="Date Range"
          />
          {(userRole === 'super_admin' || userRole === 'finance_admin') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sales Associate</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="input-base min-w-[200px]"
                data-testid="user-filter"
              >
                <option value="">All Associates</option>
                {((stats?.associates && stats.associates.length > 0)
                    ? stats.associates
                    : users
                  ).map(user => (
                  <option key={user.user_id} value={user.user_id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Lead Source</label>
            <MultiSelectDropdown
              options={[
                { value: 'manual', label: 'Manual (Added by team)' },
                { value: 'auto_imported', label: 'Auto-Imported' },
              ]}
              selected={selectedSource}
              onChange={setSelectedSource}
              placeholder="All Sources"
              testId="source-filter"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
            <MultiSelectDropdown
              options={[
                { value: 'India', label: 'India' },
                { value: 'US', label: 'US' },
                { value: 'UK', label: 'UK' },
                { value: 'Other', label: 'Other' },
              ]}
              selected={selectedCountry}
              onChange={setSelectedCountry}
              placeholder="All Countries"
              testId="country-filter"
            />
          </div>
          <button
            onClick={handleFilter}
            className="btn-primary flex items-center gap-2"
            data-testid="apply-filter"
          >
            <Filter className="w-4 h-4" />
            Apply Filter
          </button>
          {/* V82 — Super Admin only: download every row behind the cards above,
              with the filters currently APPLIED, to reconcile against Export. */}
          {userRole === 'super_admin' && (
            <button
              onClick={downloadDashboardRows}
              disabled={downloadingRows}
              title="Download the exact leads and payments behind the numbers below, for the applied filters"
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors"
              data-testid="download-dashboard-rows"
            >
              <Download className={`w-4 h-4 ${downloadingRows ? 'animate-pulse' : ''}`} />
              {downloadingRows ? 'Preparing…' : 'Download rows'}
            </button>
          )}
        </div>
      </div>

      {stats && (
        <>
          {/* Key Metrics — V34: + No-Show card, + (i) tooltips with formula/source */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title="Consultations"
              value={stats.summary.consultations_taken}
              icon={Users}
              color="bg-blue-500"
              onClick={() => openSalesLeadsModal('Consultations', 'consultations')}
              tooltip={{
                formula: 'bookings.status NOT IN {no_show, cancelled, reschedule} ∧ booking_date ∈ period',
                source: 'consultation_bookings',
                note: 'Consultation completed = any actioned stage (WIP / Enrolled / Not Interested). Excludes no-show, cancelled, reschedule.',
              }}
            />
            <StatCard
              title="No Show"
              value={stats.summary.no_show ?? 0}
              icon={UserX}
              color="bg-rose-500"
              onClick={() => openSalesLeadsModal('No Show Leads', 'no_show')}
              tooltip={{
                formula: 'bookings.status = "no_show" ∧ booking_date ∈ period',
                source: 'consultation_bookings',
                note: 'Click View Leads to open the standard lead modal for follow-up.',
              }}
            />
            <StatCard
              title="Enrolled"
              value={stats.summary.enrolled}
              icon={CheckCircle}
              color="bg-emerald-500"
              onClick={() => openSalesLeadsModal('Enrolled (New Sales)', 'enrolled')}
              tooltip={{
                formula: "distinct customers whose 1st-ever enrolment payment.captured_at ∈ period",
                source: 'payments (identity-aware)',
                note: 'First-time enrolments ONLY. Returning customers appear under Renewals, not here.',
              }}
            />
            <StatCard
              title="Conversion"
              value={`${stats.summary.conversion_rate}%`}
              subtitle={`${stats.summary.enrolled_from_consultations ?? 0} of ${stats.summary.consultations_taken ?? 0} consultations enrolled`}
              icon={Target}
              color="bg-violet-500"
              tooltip={{
                formula: 'leads whose consultation was taken in the period AND who have enrolled (any time) ÷ consultations taken in period × 100',
                source: 'consultation_bookings + payments',
                note: 'Consultations taken = WIP + Enrolled + Not Interested (excludes No-Show), paid-trial leads only. Enrolled/New Sales tiles remain payment-dated.',
              }}
            />
          </div>

          {/* V42.6 — Revenue: one card PER CURRENCY (native, no FX). New Sales =
              first-time enrolments; Renewals = returning customers. */}
          <div className="space-y-4">
            <RevenueGroup
              title="New Sales"
              subtitle="First-time enrolments"
              byCurrency={stats.summary.new_sales_by_currency}
              inrEquiv={stats.summary.new_sales_inr_equiv}
              color="bg-amber-500"
              onCardClick={(cur) => openSalesLeadsModal(`New Sales · ${cur}`, 'enrolled', cur)}
              testidPrefix="new-sales"
            />
            <RevenueGroup
              title="Renewals"
              subtitle="Returning customers (2nd+ enrolment)"
              byCurrency={stats.summary.renewals_by_currency}
              inrEquiv={stats.summary.renewals_inr_equiv}
              color="bg-green-500"
              onCardClick={(cur) => openSalesLeadsModal(`Renewals · ${cur}`, 'renewals', cur)}
              testidPrefix="renewals"
            />
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200" data-testid="total-revenue-blended">
              <span className="text-sm text-slate-600">Total revenue (blended)</span>
              <span className="text-lg font-bold text-slate-900" data-testid="total-inr-equiv">
                ≈ ₹{Number(stats.summary.total_inr_equiv || 0).toLocaleString('en-IN')}
              </span>
              <span className="text-[11px] text-slate-400">at current FX · edit in Settings</span>
            </div>
          </div>

          {stats.summary.free_coupon_enrolments > 0 && (
            <div className="bg-amber-50/70 rounded-xl border border-amber-200 px-4 py-3 flex items-center justify-between" data-testid="free-coupon-enrolments-tile">
              <div>
                <p className="text-sm font-semibold text-amber-900">Free (coupon) enrolments</p>
                <p className="text-xs text-amber-700">₹0 orders via 100%-off coupons · not revenue, not in conversion, no associate credit</p>
              </div>
              <span className="text-2xl font-bold text-amber-900" data-testid="free-coupon-enrolments-count">{stats.summary.free_coupon_enrolments}</span>
            </div>
          )}

          {/* V17 — Product Revenue: SoulAI Coach / Baby Tracker purchases. Reported
              separately — never counted as Enrolled / New Sales / Renewals. */}
          {stats.summary.product_revenue && (
            <div className="bg-white/60 rounded-xl border border-slate-200 p-4 md:p-5 space-y-4" data-testid="product-revenue-section">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Add-on Revenue</h2>
                  <p className="text-xs text-slate-500">SoulAI Coach &amp; Baby Tracker sold on your payment links · credited to whoever generated the link, excluded from the Elite tiles above</p>
                </div>
                <div className="flex items-center gap-2" data-testid="product-revenue-blended">
                  <span className="text-sm text-slate-600">Add-on revenue (blended)</span>
                  <span className="text-lg font-bold text-slate-900" data-testid="product-total-inr-equiv">
                    ≈ ₹{Number(stats.summary.product_revenue.total_inr_equiv || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
              <RevenueGroup
                title="SoulAI Coach"
                subtitle="Monthly & till-delivery plans"
                badge={`${stats.summary.product_revenue.soulai_coach?.count || 0} purchase${(stats.summary.product_revenue.soulai_coach?.count || 0) === 1 ? '' : 's'}`}
                byCurrency={stats.summary.product_revenue.soulai_coach?.by_currency}
                countByCurrency={stats.summary.product_revenue.soulai_coach?.count_by_currency}
                inrEquiv={stats.summary.product_revenue.soulai_coach?.inr_equiv}
                color="bg-violet-500"
                icon={Sparkles}
                ctaLabel="View purchases"
                onCardClick={(cur) => openSalesLeadsModal(`SoulAI Coach purchases · ${cur}`, 'products', cur, { product: 'soulai_coach' })}
                testidPrefix="product-soulai"
              />
              <RevenueGroup
                title="Baby Tracker"
                subtitle="Monthly & yearly plans"
                badge={`${stats.summary.product_revenue.baby_tracker?.count || 0} purchase${(stats.summary.product_revenue.baby_tracker?.count || 0) === 1 ? '' : 's'}`}
                byCurrency={stats.summary.product_revenue.baby_tracker?.by_currency}
                countByCurrency={stats.summary.product_revenue.baby_tracker?.count_by_currency}
                inrEquiv={stats.summary.product_revenue.baby_tracker?.inr_equiv}
                color="bg-teal-500"
                icon={Baby}
                ctaLabel="View purchases"
                onCardClick={(cur) => openSalesLeadsModal(`Baby Tracker purchases · ${cur}`, 'products', cur, { product: 'baby_tracker' })}
                testidPrefix="product-baby"
              />
            </div>
          )}

          {/* Follow-ups card removed in V32 — replaced by Past Consultation Outcomes
              on Calendar → Bookings → Past tab (with date preset + sales-rep filters
              and clickable Not Interested / WIP / No-Show / Enrolled cards). */}


          {/* User Breakdown (Admin only) */}
          {stats.user_breakdown && stats.user_breakdown.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Sales Leaderboard</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-500 border-b border-slate-200">
                      <th className="pb-3 font-medium">Associate</th>
                      <th className="pb-3 font-medium text-center">Booked</th>
                      <th className="pb-3 font-medium text-center">Completed</th>
                      <th className="pb-3 font-medium text-center">No Show</th>
                      <th className="pb-3 font-medium text-center">Enrolled</th>
                      <th className="pb-3 font-medium text-center">Conversion</th>
                      <th className="pb-3 font-medium text-center">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.user_breakdown.filter(u => !u.is_total).map((user, idx) => (
                      <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            {idx === 0 && <span className="text-amber-500">🥇</span>}
                            {idx === 1 && <span className="text-slate-400">🥈</span>}
                            {idx === 2 && <span className="text-amber-700">🥉</span>}
                            <span className="font-medium text-slate-900">{user.full_name}</span>
                          </div>
                        </td>
                        <td className="py-3 text-center text-blue-600 font-medium">{user.consultations_booked || 0}</td>
                        <td className="py-3 text-center text-slate-600">{user.consultations_completed || user.consultations_taken || 0}</td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.no_show === 0 ? 'bg-emerald-100 text-emerald-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {user.no_show || 0}
                          </span>
                        </td>
                        <td className="py-3 text-center font-semibold text-emerald-600">{user.enrolled}</td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.conversion_rate >= 40 ? 'bg-emerald-100 text-emerald-700' :
                            user.conversion_rate >= 25 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {user.conversion_rate}%
                          </span>
                        </td>
                        <td className="py-3 text-center font-semibold text-slate-900">
                          {(() => {
                            const rev = {};
                            for (const [c, v] of Object.entries(user.new_sales_by_currency || {})) rev[c] = (rev[c] || 0) + v;
                            for (const [c, v] of Object.entries(user.renewals_by_currency || {})) rev[c] = (rev[c] || 0) + v;
                            const ent = Object.entries(rev).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
                            if (!ent.length) return '₹0';
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                {ent.map(([c, v]) => (
                                  <span key={c} className={c === 'INR' ? '' : 'text-xs text-slate-500'}>{fmtCur(c, v)}</span>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Total Row */}
                  {stats.user_breakdown.find(u => u.is_total) && (
                    <tfoot>
                      <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                        <td className="py-3 text-slate-900">Total</td>
                        <td className="py-3 text-center text-blue-600">{stats.user_breakdown.find(u => u.is_total).consultations_booked}</td>
                        <td className="py-3 text-center text-slate-900">{stats.user_breakdown.find(u => u.is_total).consultations_completed}</td>
                        <td className="py-3 text-center text-red-600">{stats.user_breakdown.find(u => u.is_total).no_show}</td>
                        <td className="py-3 text-center text-emerald-600">{stats.user_breakdown.find(u => u.is_total).enrolled}</td>
                        <td className="py-3 text-center">{stats.user_breakdown.find(u => u.is_total).conversion_rate}%</td>
                        <td className="py-3 text-center text-slate-900">
                          {(() => {
                            const t = stats.user_breakdown.find(u => u.is_total);
                            const rev = {};
                            for (const [c, v] of Object.entries(t.new_sales_by_currency || {})) rev[c] = (rev[c] || 0) + v;
                            for (const [c, v] of Object.entries(t.renewals_by_currency || {})) rev[c] = (rev[c] || 0) + v;
                            const ent = Object.entries(rev).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
                            if (!ent.length) return '₹0';
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                {ent.map(([c, v]) => (
                                  <span key={c} className={c === 'INR' ? '' : 'text-xs text-slate-500'}>{fmtCur(c, v)}</span>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Leads Modal */}
      {viewLeadsModal && (
        <ViewLeadsModal
          title={viewLeadsModal.title}
          filters={viewLeadsModal.filters}
          onClose={() => setViewLeadsModal(null)}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default SalesDashboard;
